<script setup>
import { ref } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useRoiComparisonFormat } from '@/composables/roi-comparison'

import ListItem from '@/components/shared/ListItem.vue'
import RoiComparisonLibraryToggles from '@/components/roi/RoiComparisonLibraryToggles.vue'
import RoiComparisonAvatarStack from '@/components/roi/RoiComparisonAvatarStack.vue'

defineProps({
  comparison: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['open', 'edit', 'clone', 'delete'])

const i18n = getI18n()
const webSiteStore = useWebSiteStore()
const { comparisonCaption } = useRoiComparisonFormat()

const editMode = ref(false)

const onRowClick = (layout) => emit(editMode.value && webSiteStore.canManageOwned(layout, webSiteStore.permissionFlagEnum.COMPANY_SETTINGS) ? 'edit' : 'open', layout)
</script>

<template>
  <div>
    <div class="row items-center no-wrap q-px-sm q-mb-sm">
      <roi-comparison-library-toggles class="col" />
      <q-btn
        v-if="comparison.visibleComparisons.some(layout => webSiteStore.canManageOwned(layout, webSiteStore.permissionFlagEnum.COMPANY_SETTINGS))"
        flat
        round
        dense
        :icon="editMode ? 'mdi-pencil-off' : 'mdi-pencil'"
        :color="editMode ? 'primary' : 'grey-6'"
        @click="editMode = !editMode"
      >
        <q-tooltip>{{ editMode ? i18n.t('common.done') : i18n.t('common.edit') }}</q-tooltip>
      </q-btn>
    </div>

    <q-list>
      <q-item
        v-for="layout in comparison.visibleComparisons"
        :key="layout.id"
        class="q-pa-sm"
      >
        <list-item
          class="col"
          dense
          clickable
          :label="layout.name"
          :caption="comparisonCaption(layout)"
          @click="onRowClick(layout)"
        >
          <template #avatar>
            <roi-comparison-avatar-stack :type="layout.data.type" :items="comparison.layoutItems(layout)" />
          </template>
          <template #right>
            <div class="row items-center no-wrap q-gutter-x-xs">
              <q-icon
                v-if="layout.is_shared"
                name="mdi-account-group"
                color="primary"
                size="xs"
              />
              <q-btn
                flat
                round
                size="sm"
                icon="mdi-content-copy"
                @click.stop="emit('clone', layout)"
              />
              <q-btn
                v-if="editMode && webSiteStore.canManageOwned(layout, webSiteStore.permissionFlagEnum.COMPANY_SETTINGS)"
                style="padding: 0;"
                size="md"
                flat
                rounded
                color="negative"
                icon="mdi-minus-circle-outline"
                @click.stop="emit('delete', layout)"
              />
            </div>
          </template>
        </list-item>
      </q-item>
      <q-item v-if="!comparison.visibleComparisons.length">
        <q-item-section class="text-grey-6 text-center q-py-lg">{{ i18n.t('roiCompare.noSaved') }}</q-item-section>
      </q-item>
    </q-list>

  </div>
</template>
